import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { API_BASE, UPLOADS_BASE } from "../constants/api";
import "./OemMasterScreen.css";

const EMPTY_FORM = {
  oem_code: "",
  oem_name: "",
  legal_entity_type_id: "",
  country_id: "",
  product_category_id: "",
  location_id: "",
  address_line_1: "",
  address_line_2: "",
  state_province: "",
  postal_code_location_id: "",
  contact_name: "",
  designation_id: "",
  country_code: "",
  phone_numbers: [""],
  website: "",
  email_ids: [""],
  instagram_url: "",
  linkedin_url: "",
  facebook_url: "",
  company_brochure: null,
  product_catalogue: null,
  certifications: [{ certification_name: "", certification_year: "", certificate_file: null }],
};

const typeIncludes = (location, terms) => {
  const type = String(location?.location_type || "").toLowerCase();
  return terms.some((term) => type.includes(term));
};

const appendArrayFiles = (formData, data) => {
  formData.append("phone_numbers", JSON.stringify(data.phone_numbers.filter(Boolean)));
  formData.append("email_ids", JSON.stringify(data.email_ids.filter(Boolean)));
  const certifications = data.certifications
    .filter((item) => item.certification_name?.trim())
    .map((item) => ({
      certification_name: item.certification_name,
      certification_year: item.certification_year || null,
      certificate_path: item.certificate_path || null,
      has_new_file: item.certificate_file instanceof File,
    }));
  formData.append("certifications", JSON.stringify(certifications));
  data.certifications.forEach((item) => {
    if (item.certification_name?.trim() && item.certificate_file instanceof File) {
      formData.append("certification_files", item.certificate_file);
    }
  });
  if (data.company_brochure instanceof File) {
    formData.append("company_brochure", data.company_brochure);
  }
  if (data.product_catalogue instanceof File) {
    formData.append("product_catalogue", data.product_catalogue);
  }
};

const buildFormData = (data) => {
  const formData = new FormData();
  [
    "oem_code", "oem_name", "legal_entity_type_id", "country_id", "product_category_id",
    "location_id", "address_line_1", "address_line_2", "state_province",
    "postal_code_location_id", "contact_name", "designation_id", "country_code",
    "website", "instagram_url", "linkedin_url", "facebook_url",
  ].forEach((key) => formData.append(key, data[key] || ""));
  appendArrayFiles(formData, data);
  return formData;
};

const FileLink = ({ path: filePath, children }) => (
  filePath ? (
    <a href={`${UPLOADS_BASE}/${filePath}`} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : null
);

export default function OemMasterScreen() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [oems, setOems] = useState([]);
  const [legalTypes, setLegalTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    try {
      const endpoints = [
        `${API_BASE}/master/oems`,
        `${API_BASE}/master/legal-entity-types`,
        `${API_BASE}/locations`,
        `${API_BASE}/master/product-categories`,
        `${API_BASE}/master/designations`,
      ];
      const responses = await Promise.all(endpoints.map((url) => fetch(url)));
      const failed = responses.find((response) => !response.ok);
      if (failed) throw new Error("Unable to load OEM Master data.");
      const [oemData, legalData, locationData, categoryData, designationData] =
        await Promise.all(responses.map((response) => response.json()));
      setOems(oemData);
      setLegalTypes(legalData);
      setLocations(locationData);
      setCategories(categoryData);
      setDesignations(designationData);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const locationById = useMemo(
    () => new Map(locations.map((location) => [String(location.location_id), location])),
    [locations]
  );

  const hasAncestor = useCallback((location, ancestorId) => {
    let current = location;
    const seen = new Set();
    while (current?.parent_location_id && !seen.has(String(current.location_id))) {
      if (String(current.parent_location_id) === String(ancestorId)) return true;
      seen.add(String(current.location_id));
      current = locationById.get(String(current.parent_location_id));
    }
    return false;
  }, [locationById]);

  const countries = useMemo(
    () => locations.filter((location) => typeIncludes(location, ["country"])),
    [locations]
  );
  const cityDistricts = useMemo(
    () => locations.filter((location) =>
      typeIncludes(location, ["city", "district"])
      && (!form.country_id || hasAncestor(location, form.country_id))
    ),
    [form.country_id, hasAncestor, locations]
  );
  const postalCodes = useMemo(
    () => locations.filter((location) =>
      typeIncludes(location, ["postal", "postcode", "pin code", "pincode", "zip"])
      && (!form.location_id
        || String(location.parent_location_id) === String(form.location_id)
        || hasAncestor(location, form.location_id))
    ),
    [form.location_id, hasAncestor, locations]
  );

  const deriveState = useCallback((locationId) => {
    let current = locationById.get(String(locationId));
    const seen = new Set();
    while (current && !seen.has(String(current.location_id))) {
      if (typeIncludes(current, ["state", "province"])) return current.location_name;
      seen.add(String(current.location_id));
      current = locationById.get(String(current.parent_location_id));
    }
    return "";
  }, [locationById]);

  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const changeCountry = (value) => {
    const country = locationById.get(String(value));
    setForm((current) => ({
      ...current,
      country_id: value,
      country_code: country?.country_code || "",
      location_id: "",
      state_province: "",
      postal_code_location_id: "",
    }));
  };

  const changeLocation = (value) => {
    setForm((current) => ({
      ...current,
      location_id: value,
      state_province: deriveState(value),
      postal_code_location_id: "",
    }));
  };

  const updateList = (field, index, value) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].map((item, itemIndex) => itemIndex === index ? value : item),
    }));
  };

  const addListItem = (field) => {
    setForm((current) => ({ ...current, [field]: [...current[field], ""] }));
  };

  const removeListItem = (field, index) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].length === 1
        ? [""]
        : current[field].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateCertification = (index, key, value) => {
    setForm((current) => ({
      ...current,
      certifications: current.certifications.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addCertification = () => {
    setForm((current) => ({
      ...current,
      certifications: [
        ...current.certifications,
        { certification_name: "", certification_year: "", certificate_file: null },
      ],
    }));
  };

  const removeCertification = (index) => {
    setForm((current) => ({
      ...current,
      certifications: current.certifications.length === 1
        ? [{ certification_name: "", certification_year: "", certificate_file: null }]
        : current.certifications.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.stopPropagation();
      event.currentTarget.classList.add("was-validated");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(
        editingId ? `${API_BASE}/master/oems/${editingId}` : `${API_BASE}/master/oems`,
        { method: editingId ? "PUT" : "POST", body: buildFormData(form) }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Unable to save OEM.");
      setMessage(editingId ? "OEM updated successfully." : "OEM created successfully.");
      resetForm();
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const edit = (oem) => {
    setEditingId(oem.oem_id);
    setForm({
      ...EMPTY_FORM,
      ...oem,
      legal_entity_type_id: String(oem.legal_entity_type_id || ""),
      country_id: String(oem.country_id || ""),
      product_category_id: String(oem.product_category_id || ""),
      location_id: String(oem.location_id || ""),
      postal_code_location_id: String(oem.postal_code_location_id || ""),
      designation_id: String(oem.designation_id || ""),
      phone_numbers: oem.phone_numbers?.length ? oem.phone_numbers : [""],
      email_ids: oem.email_ids?.length ? oem.email_ids : [""],
      certifications: oem.certifications?.length
        ? oem.certifications.map((item) => ({ ...item, certificate_file: null }))
        : EMPTY_FORM.certifications,
      company_brochure: null,
      product_catalogue: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (oem) => {
    if (!window.confirm(`Delete ${oem.oem_name}?`)) return;
    try {
      const response = await fetch(`${API_BASE}/master/oems/${oem.oem_id}`, { method: "DELETE" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Unable to delete OEM.");
      if (editingId === oem.oem_id) resetForm();
      setMessage("OEM deleted successfully.");
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div className="oem-master">
      <Form noValidate onSubmit={submit} className="oem-form">
        <section className="oem-section">
          <h5>Company Details</h5>
          <Row className="g-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>OEM ID</Form.Label>
              <Form.Control value={form.oem_code || "System generated"} readOnly />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Name *</Form.Label>
              <Form.Control required value={form.oem_name} onChange={(e) => change("oem_name", e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Legal Status *</Form.Label>
              <Form.Select required value={form.legal_entity_type_id} onChange={(e) => change("legal_entity_type_id", e.target.value)}>
                <option value="">Select legal entity type</option>
                {legalTypes.map((item) => <option key={item.legal_entity_type_id} value={item.legal_entity_type_id}>{item.legal_entity_type}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Country *</Form.Label>
              <Form.Select required value={form.country_id} onChange={(e) => changeCountry(e.target.value)}>
                <option value="">Select country</option>
                {countries.map((item) => <option key={item.location_id} value={item.location_id}>{item.location_name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Category *</Form.Label>
              <Form.Select required value={form.product_category_id} onChange={(e) => change("product_category_id", e.target.value)}>
                <option value="">Select product category</option>
                {categories.map((item) => <option key={item.product_category_id} value={item.product_category_id}>{item.product_category}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Location (City / District) *</Form.Label>
              <Form.Select required disabled={!form.country_id} value={form.location_id} onChange={(e) => changeLocation(e.target.value)}>
                <option value="">Select city or district</option>
                {cityDistricts.map((item) => <option key={item.location_id} value={item.location_id}>{item.location_name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Address Line 1 *</Form.Label>
              <Form.Control required value={form.address_line_1} onChange={(e) => change("address_line_1", e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control value={form.address_line_2} onChange={(e) => change("address_line_2", e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>State / Province</Form.Label>
              <Form.Control readOnly value={form.state_province} placeholder="Auto-filled from location" />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Postal Code</Form.Label>
              <Form.Select disabled={!form.location_id} value={form.postal_code_location_id} onChange={(e) => change("postal_code_location_id", e.target.value)}>
                <option value="">Select postal code</option>
                {postalCodes.map((item) => <option key={item.location_id} value={item.location_id}>{item.location_name}</option>)}
              </Form.Select>
            </Form.Group>
          </Row>
        </section>

        <section className="oem-section">
          <h5>Contact Details</h5>
          <Row className="g-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Contact Name *</Form.Label>
              <Form.Control required value={form.contact_name} onChange={(e) => change("contact_name", e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Designation</Form.Label>
              <Form.Select value={form.designation_id} onChange={(e) => change("designation_id", e.target.value)}>
                <option value="">Select designation</option>
                {designations.map((item) => <option key={item.designation_id} value={item.designation_id}>{item.designation}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Country Code *</Form.Label>
              <Form.Control required value={form.country_code} onChange={(e) => change("country_code", e.target.value)} placeholder="+91" />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Phone No.</Form.Label>
              {form.phone_numbers.map((phone, index) => (
                <div className="oem-repeat-row" key={`phone-${index}`}>
                  <Form.Control type="tel" value={phone} onChange={(e) => updateList("phone_numbers", index, e.target.value)} />
                  <Button variant="outline-danger" onClick={() => removeListItem("phone_numbers", index)}>−</Button>
                </div>
              ))}
              <Button size="sm" variant="outline-primary" onClick={() => addListItem("phone_numbers")}>+ Add phone</Button>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Email ID</Form.Label>
              {form.email_ids.map((email, index) => (
                <div className="oem-repeat-row" key={`email-${index}`}>
                  <Form.Control type="email" value={email} onChange={(e) => updateList("email_ids", index, e.target.value)} />
                  <Button variant="outline-danger" onClick={() => removeListItem("email_ids", index)}>−</Button>
                </div>
              ))}
              <Button size="sm" variant="outline-primary" onClick={() => addListItem("email_ids")}>+ Add email</Button>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Website</Form.Label>
              <Form.Control type="url" value={form.website || ""} onChange={(e) => change("website", e.target.value)} placeholder="https://" />
            </Form.Group>
          </Row>
        </section>

        <section className="oem-section">
          <h5>Social Media & Documents</h5>
          <Row className="g-3">
            {[
              ["instagram_url", "Instagram Page"],
              ["linkedin_url", "LinkedIn"],
              ["facebook_url", "Facebook"],
            ].map(([key, label]) => (
              <Form.Group as={Col} md={4} key={key}>
                <Form.Label>{label}</Form.Label>
                <Form.Control type="url" value={form[key] || ""} onChange={(e) => change(key, e.target.value)} placeholder="https://" />
              </Form.Group>
            ))}
            <Form.Group as={Col} md={6}>
              <Form.Label>Company Brochure (PDF)</Form.Label>
              <Form.Control type="file" accept=".pdf,application/pdf" onChange={(e) => change("company_brochure", e.target.files?.[0] || null)} />
              <FileLink path={form.company_brochure_path}>View current brochure</FileLink>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Product Catalogue (PDF)</Form.Label>
              <Form.Control type="file" accept=".pdf,application/pdf" onChange={(e) => change("product_catalogue", e.target.files?.[0] || null)} />
              <FileLink path={form.product_catalogue_path}>View current catalogue</FileLink>
            </Form.Group>
          </Row>
        </section>

        <section className="oem-section">
          <div className="oem-section-heading">
            <h5>Certifications</h5>
            <Button size="sm" variant="outline-primary" onClick={addCertification}>+ Add certification</Button>
          </div>
          {form.certifications.map((certification, index) => (
            <Row className="g-2 oem-cert-row" key={`certification-${index}`}>
              <Form.Group as={Col} md={4}>
                <Form.Control placeholder="Certification name" value={certification.certification_name || ""} onChange={(e) => updateCertification(index, "certification_name", e.target.value)} />
              </Form.Group>
              <Form.Group as={Col} md={2}>
                <Form.Control type="number" min="1900" max="2100" placeholder="Year" value={certification.certification_year || ""} onChange={(e) => updateCertification(index, "certification_year", e.target.value)} />
              </Form.Group>
              <Form.Group as={Col} md={5}>
                <Form.Control type="file" accept=".pdf,application/pdf" onChange={(e) => updateCertification(index, "certificate_file", e.target.files?.[0] || null)} />
                <FileLink path={certification.certificate_path}>View current certificate</FileLink>
              </Form.Group>
              <Col md={1}>
                <Button variant="outline-danger" onClick={() => removeCertification(index)}>−</Button>
              </Col>
            </Row>
          ))}
        </section>

        <div className="oem-actions">
          {editingId && <Button variant="outline-secondary" onClick={resetForm}>Cancel edit</Button>}
          <Button type="submit" variant="success" disabled={saving}>
            {saving ? "Saving..." : editingId ? "Update OEM" : "Save OEM"}
          </Button>
        </div>
      </Form>

      {message && <div className={`oem-message ${message.startsWith("Error") ? "error" : "success"}`}>{message}</div>}

      <section className="oem-list">
        <h5>OEM Records</h5>
        <div className="table-responsive">
          <Table hover>
            <thead><tr><th>OEM ID</th><th>Name</th><th>Legal Status</th><th>Country</th><th>Category</th><th>Contact</th><th>Actions</th></tr></thead>
            <tbody>
              {oems.length === 0 && <tr><td colSpan="7" className="text-center text-muted">No OEM records found.</td></tr>}
              {oems.map((oem) => (
                <tr key={oem.oem_id}>
                  <td>{oem.oem_code}</td>
                  <td>{oem.oem_name}</td>
                  <td>{oem.legal_entity_type}</td>
                  <td>{oem.country_name}</td>
                  <td>{oem.product_category}</td>
                  <td>{oem.contact_name}<br /><small>{oem.country_code} {oem.phone_numbers?.[0] || ""}</small></td>
                  <td className="oem-table-actions">
                    <Button size="sm" variant="outline-primary" onClick={() => edit(oem)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(oem)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </section>
    </div>
  );
}
